import {
  Avatar,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemDecorator,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { ApiVenueDetail } from "griptonite-api-client";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useMeProfileQuery } from "@/hooks/profile";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import { useAvailableVenuesQuery } from "../../hooks/venues";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

export const VenueItem = ({ venue }: { venue: ApiVenueDetail }) => {
  return (
    <ListItemDecorator>
      <Box sx={{ marginRight: 1 }}>
        <Avatar
          src={getThumbnailUrl(venue.logo?.url) ?? undefined}
          alt={venue.name}
          sx={{
            width: 24,
            height: 24,
            borderRadius: 4,
          }}
        />
      </Box>
      <Typography fontWeight="bold" fontSize={14}>
        {venue.name}
      </Typography>
    </ListItemDecorator>
  );
};

type Props<T extends boolean | undefined> = {
  name: string;
  label: string;
  multiple?: T;
  onlyStaffVenues?: boolean;
};

type ValueType<T> = T extends true ? string[] : string | null;

export default function VenueSelectInput<T extends boolean | undefined = undefined>({
  name,
  label,
  multiple,
  onlyStaffVenues,
}: Props<T>) {
  const { control } = useFormContext();
  useMeProfileQuery();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const { data: availableVenues } = useAvailableVenuesQuery();
  const venues = useMemo(() => {
    return availableVenues?.filter((venue) => {
      if (onlyStaffVenues) {
        return ["STAFF", "ADMIN"].includes(venue.userRole || "");
      }
      return true;
    });
  }, [availableVenues, onlyStaffVenues]);

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <Select
        slotProps={{
          button: {
            sx: {
              flexWrap: "wrap",
              gap: 1,
            },
          },
        }}
        multiple={multiple}
        value={field.value}
        onChange={(_, value) => field.onChange(value as unknown as ValueType<T>)}
        onBlur={field.onBlur}
        renderValue={(value) => {
          if (Array.isArray(value)) {
            return value.map((option) => {
              const venue = venues?.find((venue) => venue.uuid === option.value);
              if (!venue) {
                return null;
              }
              return <VenueItem key={option.value} venue={venue} />;
            });
          } else {
            const venue = venues?.find((venue) => venue.uuid === value?.value);
            if (!venue) {
              return null;
            }
            return <VenueItem key={value?.value} venue={venue} />;
          }
        }}
      >
        {venues?.map((venue) => (
          <Option key={venue.uuid} value={venue.uuid} label={venue.name}>
            <VenueItem venue={venue} />
          </Option>
        ))}
      </Select>
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  );
}
